import { Component, OnInit, Input, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';

import { DataService, HelperService, HTTPService, FormatService, DateService } from 'app/services';
import { datetimeFormat } from 'app/constants/date-time-format';
import { config } from 'app/constants';
import { currency } from 'app/constants/currency.constants';
import { escapeRegExp } from 'lodash';
import { take } from 'rxjs/operators';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-global-search-item',
  templateUrl: './global-search-item.component.html',
  styleUrls: ['./global-search-item.component.scss'],
})
export class GlobalSearchItemComponent implements OnInit, OnDestroy {
  enhancedGlobalSearchData: any = {};
  @Input() data: any = {};
  @Input() defaultPriority: any = {};
  @Input() query: string = '';
  @Input() close: any;
  @Input() isROPrioritiesEnabled: boolean = false;
  @Input() isEnhancedGlobalSearchEnabled: boolean = false;
  @Input() isMyFleetCenterEnabled: boolean = false;
  @Output() clearModal = new EventEmitter();
  @Input() globalSearchIncludeArchivedOrders: boolean = false;
  // @Input() archivedOrders: boolean = false;

  totalPages: number = 0;
  // archivedOrders: boolean = true;

  limit: number = 8;
  isCustomerProgressLoadEnabled: boolean;
  isOrderProgressLoadEnabled: boolean;
  defaultTitle: string = config.defaultTitle;
  usd: string = currency.USD;

  totalCustomerRecords: number;
  customers: any[] = [];
  customerPageIndex: number = 0;
  isCustomerLoading: boolean = false;
  totalVehicleRecords: number;
  vehicles: any[] = [];
  vehiclePageIndex: number = 0;
  isVehicleLoading: boolean = false;
  totalOrderRecords: number;
  orders: any[] = [];
  orderPageIndex: number = 0;
  isOrderLoading: boolean = false;

  subscription: Subscription;
  isFirstLoad: boolean = false;
  show: boolean = true;

  constructor(
    private dataService: DataService,
    public helperService: HelperService,
    private httpService: HTTPService,
    private formatService: FormatService,
    private router: Router,
    public dateService: DateService
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit() {
    this.subscription = this.dataService.getSearchEvent().subscribe((res) => {
      this.query = res;
      this.getDefaultData();
    });
    if (this.isEnhancedGlobalSearchEnabled) {
      if (!this.isFirstLoad) {
        if (this.query) this.getDefaultData();
        this.isFirstLoad = true;
      }
    }
  }

  closeModal() {
    this.clearModal.emit(true);
  }

  async getDefaultData() {
    if (this.query.length >= 2) {
      this.isCustomerLoading = true;
      this.isVehicleLoading = true;
      this.isOrderLoading = true;
      const data: any = await this.handleSearch('all');
      this.customers = data.customers?.data;
      this.vehicles = data.vehicles?.data;
      this.orders = data.orders?.data;
      this.totalCustomerRecords = data.customers?.metadata.totalRecords;
      this.totalVehicleRecords = data.vehicles?.metadata.totalRecords;
      this.totalOrderRecords = data.orders?.metadata.totalRecords;
      this.isCustomerLoading = false;
      this.isVehicleLoading = false;
      this.isOrderLoading = false;
      this.customerPageIndex = 0;
      this.vehiclePageIndex = 0;
      this.orderPageIndex = 0;
    }
  }
  async onCustomerScroll(event) {
    const element = event.target;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < 10) {
      if (this.customers.length < this.totalCustomerRecords) {
        this.isCustomerLoading = true;
        this.customerPageIndex = this.customerPageIndex + 1;
        const data: any = await this.handleSearch('customer', this.customerPageIndex);
        if (this.customerPageIndex) {
          this.customers = [...this.customers, ...data.customers?.data];
        }
        this.isCustomerLoading = false;
      }
    }
  }

  async onVehicleScroll(event) {
    const element = event.target;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < 10) {
      if (this.vehicles.length < this.totalVehicleRecords) {
        this.isVehicleLoading = true;
        this.vehiclePageIndex = this.vehiclePageIndex + 1;
        const data: any = await this.handleSearch('vehicle', this.vehiclePageIndex);
        if (this.vehiclePageIndex) {
          this.vehicles = [...this.vehicles, ...data.vehicles?.data];
        }
        this.isVehicleLoading = false;
      }
    }
  }

  async onOrdersScroll(event) {
    const element = event.target;

    if (element.scrollHeight - element.scrollTop - element.clientHeight < 10) {
      if (this.orders.length < this.totalOrderRecords) {
        this.isOrderLoading = true;
        this.orderPageIndex = this.orderPageIndex + 1;
        const data: any = await this.handleSearch('order', this.orderPageIndex);
        if (this.orderPageIndex) {
          this.orders = [...this.orders, ...data.orders?.data];
        }
        this.isOrderLoading = false;
      }
    }
  }

  async handleSearch(type: string, pageIndex: number = 0) {
    this.query = this.query;
    const config: any = {
      limit: this.limit,
      skip: pageIndex * this.limit,
      type: type,
      includeArchived: this.globalSearchIncludeArchivedOrders,
    };

    let query = this.query;

    if (/^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/.test(this.query)) {
      query = this.query.replace(/[\ \(\)\-]+/g, '');
    } else if (/^\([0-9]+\) [0-9]+-[0-9]+$/.test(this.query) || /^[0-9]+-[0-9]+$/.test(this.query)) {
      query = this.query.replace(/[\ \(\)\-]+/g, '');
    }

    const results: any = await this.httpService.globalSearchEnhanced(config, { search: encodeURI(query) });
    if (results && results.data && results.data.searchResult) {
      this.enhancedGlobalSearchData = results.data.searchResult;
      const currentDateTime = this.dateService.getCurrentDateTimeTz(results.serverTime).getTime();
      if (this.enhancedGlobalSearchData?.customers?.data?.length) {
        this.enhancedGlobalSearchData.customers.data.forEach((record: any) => {
          if (record.customer?.contactInfo?.length) {
            record.customer.mobileInfo = [];
            record.customer.emailInfo = [];
            record.customer.contactInfo.forEach((contact: any) => {
              if (
                (contact.contactType === 'mobile' && (contact.value?.includes(this.query) || this.fomratValues(contact.value, 'phone')?.includes(this.query))) ||
                (contact.contactType === 'mobile' && contact.isPrimary)
              )
                record.customer.mobileInfo.push(contact);
              if ((contact.contactType === 'email' && contact.value?.includes(this.query)) || (contact.contactType === 'email' && contact.isPrimary)) record.customer.emailInfo.push(contact);
            });
          }
          if (record.customer?.appointments?.length) {
            record.customer.appointments = record.customer?.appointments
              .filter(({ startDateTime }) => this.dateService.getCurrentDateTimeTz(new Date(startDateTime).getTime()).getTime() > currentDateTime)
              .map((appointment: any) => {
                appointment = this.formatAppointment(appointment);
                return appointment;
              });
          }
        });
      }
    }
    return this.enhancedGlobalSearchData;
  }

  formatAppointment(appointment: any) {
    let startDateTime: any;

    if (this.helperService.isAppleDevice()) {
      startDateTime = new Date(this.dateService.madeDateStringIOSCompatible(appointment.start));
    } else {
      startDateTime = new Date(appointment.start);
    }

    return {
      ...appointment,
      startDateTime: this.dateService.formatDate(startDateTime.getTime(), datetimeFormat.mmmdyyhhmmaa),
      title: appointment.title || config.defaultTitle,
      month: this.dateService.formatDate(startDateTime, 'MMM'),
      year: this.dateService.formatDate(startDateTime, 'YYYY'),
      date: this.dateService.formatDate(startDateTime, 'DD'),
      time: this.dateService.formatDate(startDateTime, 'hh:mm a'),
    };
  }

  openAppointmentModal(appointment: any) {
    this.dataService.sendData(this.dataService.typeDialog('add-appointment', 'calendar-view', appointment._id, null, appointment));
  }

  openCustomerPanel(event: Event) {
    event.stopPropagation();
    this.dataService.sendData(this.dataService.typeSidebar('customer', this.data._id, this.data.fullName, { _id: this.data._id }));
    if (this.close) {
      this.close();
    }
  }

  openVehiclePanel(event: Event, vehicle: any) {
    event.stopPropagation();
    this.dataService.sendData(this.dataService.typeSidebar('vehicle', this.data._id, this.data.fullName, { _id: this.data._id, vehicleId: vehicle.vehicleId }));
    if (this.close) {
      this.close();
    }
  }

  openSidePanel(data: any, tab: any) {
    /**
     * This data is subscribe in sidebar.component
     */
    data.customer._id = data.customer.customerId;
    if (tab === 'vehicle' && data.vehicle?.vehicleId) {
      data.customer.vehicleId = data.vehicle?.vehicleId;
      data.customer.zIndex = true;
    }

    if (tab === 'driver') {
      data.customer = {
        ...data.customer?.customer,
        driverId: data.driverId,
      }
    }

    this.dataService.sendData(this.dataService.typeSidebar(tab, data.customer.customerId, data.customer.displayName, data.customer));
  }

  openEstimate(estimate: any, event: Event) {
    event.stopPropagation();
    estimate._id = estimate._id || estimate.orderId;
    this.helperService.openPageInNewExistingTab(`/estimate/${estimate._id}`, estimate._id);
    if (this.close) {
      this.close();
    }
  }

  renderText(text: any, className?: string, isEnhancedGlobalSearch: boolean = false, isNumber = false) {
    if (text) {
      let str;
      if (isNumber) {
        str = text;
      } else {
        str = text.replace(/\s/g, '&nbsp;');
      }

      if (className === 'phone') {
        str = this.formatService.formatPhoneNumber(str);
      }
      if (className === 'invoiceTotal') {
        str = this.formatService.formatCurrency(parseInt(str));
      }

      //higlight the text as bold if the search item conatains the search term.
      const regex = new RegExp(escapeRegExp(`${this.query.replace(/\s/g, '&nbsp;')}`), 'ig');
      if (str && this.query && str.includes(this.query) && !isEnhancedGlobalSearch) {
        str = str.replace(regex, `<span class="global-search-highlight ${className}"><b>${this.query}</b></span>`);
      }
      if (str && this.query && str.includes(this.query) && isEnhancedGlobalSearch) {
        str = str.replace(regex, `<span class="highlighted-text"><b>${this.query}</b></span>`);
      }
      return str;
    }

    return text;
  }

  fomratValues(value, key) {
    if (key === 'phone') return this.formatService.formatPhoneNumber(value);
    if (key === 'invoiceTotal') return this.formatService.formatCurrency(Number(value));
  }

  editAppointment(appointmentId, event) {
    event.stopPropagation();
    this.httpService.getAppointmentById(appointmentId).then(
      (appt) => {
        if (appt.status !== 'cancelled') {
          this.dataService.sendData(this.dataService.typeDialog('add-appointment', 'calendar-view', appointmentId, null, appt));
        } else {
          this.helperService.showToaster('error', 'Appointment is canceled');
        }
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );

    if (this.close) {
      this.close();
    }
  }

  handleChange(event) {
    this.globalSearchIncludeArchivedOrders = event;
    this.getDefaultData();
  }
}
